import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProjectHeader = ({ project, onUpdate, userRole, navigate }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editedName, setEditedName] = useState(project?.name);
  const [editedStartDate, setEditedStartDate] = useState(project?.estimatedStartDate);
  const [editedEndDate, setEditedEndDate] = useState(project?.estimatedEndDate);

  const handleSaveName = () => {
    onUpdate({ ...project, name: editedName });
    setIsEditingName(false);
  };

  const handleSaveDates = () => {
    onUpdate({ 
      ...project, 
      estimatedStartDate: editedStartDate,
      estimatedEndDate: editedEndDate 
    });
    setIsEditingDates(false);
  };

  const handleCancelName = () => {
    setEditedName(project?.name);
    setIsEditingName(false);
  };

  const handleCancelDates = () => {
    setEditedStartDate(project?.estimatedStartDate);
    setEditedEndDate(project?.estimatedEndDate);
    setIsEditingDates(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateDuration = () => {
    const start = new Date(project.estimatedStartDate);
    const end = new Date(project.estimatedEndDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return days;
  };

  const isBoss = userRole === 'boss';

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            {isEditingName && isBoss ? (
              <div className="flex-1 flex items-center gap-2">
                <Input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e?.target?.value)}
                  className="flex-1"
                  placeholder="Project name"
                />
                <Button
                  variant="default"
                  size="sm"
                  iconName="Check"
                  onClick={handleSaveName}
                  disabled={!editedName?.trim()}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={handleCancelName}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground">{project?.name}</h1>
                {isBoss && (
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                    aria-label="Edit project name"
                  >
                    <Icon name="Pencil" size={16} strokeWidth={2} />
                  </button>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
            {isEditingDates && isBoss ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1">
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={editedStartDate}
                    onChange={(e) => setEditedStartDate(e?.target?.value)}
                    className="w-full sm:w-auto"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={editedEndDate}
                    onChange={(e) => setEditedEndDate(e?.target?.value)}
                    className="w-full sm:w-auto"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Check"
                    onClick={handleSaveDates}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={handleCancelDates}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Calendar" size={18} strokeWidth={2} />
                  <span className="text-sm font-medium">
                    {formatDate(project?.estimatedStartDate)} - {formatDate(project?.estimatedEndDate)}
                  </span>
                  <span className="text-xs bg-muted px-2 py-1 rounded">
                    {calculateDuration()} days
                  </span>
                </div>
                {isBoss && (
                  <button
                    onClick={() => setIsEditingDates(true)}
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                    aria-label="Edit project dates"
                  >
                    <Icon name="Pencil" size={16} strokeWidth={2} />
                  </button>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              project?.status === 'ongoing' ?'bg-primary/10 text-primary' :'bg-success/10 text-success'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                project?.status === 'ongoing' ? 'bg-primary' : 'bg-success'
              }`} />
              {project?.status === 'ongoing' ? 'Ongoing' : 'Completed'}
            </div>
            <div className="text-sm text-muted-foreground">
              {project?.completedPhases} of {project?.totalPhases} phases completed
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            iconName="MessageSquare"
            iconPosition="left"
            onClick={() => navigate('/phase-management', { state: { projectId: project?.id, projectName: project?.name } })}
          >
            Contact Team
          </Button>
          {isBoss && (
            <Button
              variant="default"
              iconName="Users"
              iconPosition="left"
            >
              Manage Team
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectHeader;